/* WRAPPED IN AN IIFE BECAUSE THE STOCK PAGE NOW LOADS EVERY APP AT ONCE (15/08/2026).
   These were four separate pages, so each could declare `escapeHtml`, `spinnerHtml`,
   `MONTHS` and friends at the top level harmlessly. On one page they share ONE global
   scope, and a second `const escapeHtml` is a SyntaxError that kills the WHOLE script -
   so `customElements.define` never ran and every tab after Stock check sat on its
   spinner for ever. Nothing here is used across files; the wrapper simply stops them
   seeing each other. Same shape as the `money` collision in the sync's inlined modules. */
(function () {
/**
 * Stock processing - booking in stock that has been invoiced but has not arrived.
 *
 * A delivery note adds stock to the till, but the boxes are not in the shop yet.
 * EposNow is allowed to disagree with the shelf, because nobody can sell in store a
 * thing that is not physically there - the shop floor corrects itself. The WEBSITE
 * does not: a customer can buy anything it says exists. So delivered stock is held
 * back from Shopify until somebody here says the boxes landed.
 *
 * Ticking a line is therefore the moment stock becomes buyable online, which is why
 * this page is deliberately plain: one number, one button, and the number it started
 * at shown separately so a mis-tap is obvious.
 */
class StockProcessing extends HTMLElement {
  connectedCallback() {
    this.url = this.dataset.url;
    this.secret = this.dataset.secret;
    this.panel = this.querySelector('[data-panel]');
    this.statusLine = this.querySelector('[data-status]');
    // THE PICTURES WIRE THEMSELVES (`staff-pictures.js`, shared with the other staff pages):
    // a tap opens every picture side by side, which is the real interaction on a tablet in
    // the stockroom, and a desk gets a hover preview on top.
    window.StaffPictures.bind(this.panel);
    this.pendingBox = this.querySelector('[data-pending]');
    this.pendingText = this.querySelector('[data-pending-text]');
    this.querySelector('[data-retry]').addEventListener('click', () => this.flush());

    // A tick that failed to save. Kept in memory only: the boxes are on the floor
    // in front of whoever is doing this, so a lost tick is re-tickable - whereas
    // replaying a stale one after a reload could push stock twice.
    this.pending = [];
    // Products with a tick on the wire from this page, by till id - see `claim`.
    this.inFlight = new Set();

    // APPROVING A DELIVERY CHANGES THIS LIST, so listen for it rather than showing
    // whatever was true when the page loaded (Jonathan, 15/08/2026). The tab strip only
    // hides and shows, so without this the list is as stale as the page.
    //
    // Debounced, because approving a note line by line fires this once per line and the
    // useful moment is after the last one. 400ms is longer than a run of clicks and far
    // shorter than walking to the shelf.
    //
    // A RELOAD IS REFUSED WHILE A TICK IS WAITING TO BE RETRIED. `this.pending` holds
    // ticks that failed to save and are kept in memory only, deliberately, because the
    // boxes are on the floor in front of whoever is counting - re-rendering underneath
    // them would take those ticks off the screen and the person would have no way of
    // knowing which ones to do again.
    this.reloadTimer = null;
    this.onStockChanged = () => {
      clearTimeout(this.reloadTimer);
      this.reloadTimer = setTimeout(() => {
        if (this.pending.length) return;
        this.load();
      }, 400);
    };
    document.addEventListener('dc:stock-on-order-changed', this.onStockChanged);

    // Fetched when the tab is first opened, not with the page (15/10/2026).
    (window.dcWhenShown || ((el, fn) => fn()))(this, () => this.load());
  }

  // Removed with the element, so a page that swaps sections about cannot leave a listener
  // behind holding a reference to a dead app.
  disconnectedCallback() {
    document.removeEventListener('dc:stock-on-order-changed', this.onStockChanged);
    clearTimeout(this.reloadTimer);
  }

  async call(operation, body, fresh) {
    const answer = await (window.dcFetch || fetch)(this.url, {
      method: 'POST',
      cache: fresh ? 'no-store' : 'default',
      headers: { 'Content-Type': 'application/json' },
      // The staff name travels with the tick so the sheet records WHO booked it in.
      // Taken from the logged-in customer, not typed, so it cannot be someone else's.
      body: JSON.stringify({
        secret: this.secret, operation, staffName: this.dataset.customerName || '', ...body,
      }),
    });
    if (!answer.ok) throw new Error(`${operation} failed (${answer.status})`);
    const text = await answer.text();
    // A 200 WITH AN EMPTY BODY IS NOT A SUCCESS. The manager's Problems list was
    // silently blank for 26 hours on exactly this - from outside, the call worked.
    if (!text.trim()) throw new Error(`${operation} answered with nothing at all`);
    return JSON.parse(text);
  }

  async load() {
    // ASKED AT THE SAME TIME AS THE LIST, not after it (15/10/2026): the two do not depend on
    // each other, and waiting cost three seconds on every load.
    const preOrders = this.loadPreOrders();
    try {
      const data = await this.call('list', {});
      // THE PRE-ORDER FIGURES COME FROM THEIR OWN ENDPOINT, not from this one.
      //
      // WHY NOT ADD THEM TO `Stock Processing Ops`: that workflow is the live arrival path
      // - it writes till stock when somebody ticks Arrived, and a mistake in it loses a
      // delivery. `Pre-Orders Ops` already answers exactly this question and is proved, so
      // the page asks it rather than the arrival path growing a Shopify dependency.
      //
      // IT IS CHEAP WHEN THERE IS NOTHING TO FIND (his constraint: "We don't need to check
      // Shopify if none of the products in the stock processing page are preorders"). That
      // endpoint skips Shopify entirely when nothing is flagged, so the cost of asking is a
      // couple of small table reads.
      //
      // AND IT NEVER BLOCKS THE PAGE. A failure here loses the PO badge and the presold
      // line; it must not lose the delivery list, which is what people are here for.
      this.preOrders = await preOrders;
      this.dates = await this.loadDates(data.awaiting || []);
      this.render(data);
    } catch (err) {
      this.panel.innerHTML = '';
      this.say(`Could not load the deliveries: ${err.message}`, true);
    }
  }

  async loadPreOrders() {
    const url = this.dataset.preorderUrl;
    if (!url) return new Map();
    try {
      const answer = await (window.dcFetch || fetch)(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ secret: this.dataset.preorderSecret || this.secret,
                               operation: 'list' }),
      });
      if (!answer.ok) return new Map();
      const text = await answer.text();
      if (!text.trim()) return new Map();
      const data = JSON.parse(text);
      return new Map((data.products || []).map((p) => [Number(p.productId), p]));
    } catch (err) {
      return new Map();
    }
  }

  /**
   * RELEASE AND SELL-FROM DATES (Jonathan, 15/10 and 18/10/2026): staff can see what is out
   * now and what is held for a later day. Both dates are worked out by `Pre-Orders Ops`
   * from the one rule list (`PreOrderRules.js`, the Pre-Order Sale Rules page) - the same
   * one that decides when a name drops "PRE ORDER" - so this page never carries a copy of
   * any game's early-sale rule. Like the pre-order figures it never blocks the list.
   */
  async loadDates(awaiting) {
    const url = this.dataset.preorderUrl;
    const tillIds = [...new Set(awaiting.map((r) => Number(r.tillId)).filter(Boolean))];
    if (!url || !tillIds.length) return new Map();
    try {
      const answer = await (window.dcFetch || fetch)(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ secret: this.dataset.preorderSecret || this.secret,
                               operation: 'dates', tillIds }),
      });
      if (!answer.ok) return new Map();
      const text = await answer.text();
      if (!text.trim()) return new Map();
      const data = JSON.parse(text);
      return new Map((data.dates || []).map((d) => [Number(d.tillId), d]));
    } catch (err) {
      return new Map();
    }
  }

  /**
   * The date line under a product's name. Held for a later day says when it may go out and
   * when it is officially released; one already on sale says so. Nothing when no date is known.
   */
  static dateLine(dates, todayIso) {
    if (!dates || !dates.sellFrom) return '';
    // "Release date: Fri 16th Oct" (Jonathan, 18/10/2026) - the day the shop may sell it, which
    // is what matters on the shop floor; the official date is not shown.
    const show = (iso) => {
      const d = new Date(iso + 'T12:00:00Z');
      const day = d.getUTCDate();
      const th = (day % 100 >= 11 && day % 100 <= 13) ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th');
      const part = (o) => d.toLocaleDateString('en-GB', Object.assign({ timeZone: 'Europe/London' }, o));
      return `${part({ weekday: 'short' })} ${day}${th} ${part({ month: 'short' })}`;
    };
    if (dates.sellFrom > todayIso) {
      return `<span class="stock-processing__date is-held">Release date: ${show(dates.sellFrom)}</span>`;
    }
    return `<span class="stock-processing__date">Out now</span>`;
  }

  say(message, bad) {
    this.statusLine.hidden = !message;
    this.statusLine.textContent = message || '';
    this.statusLine.classList.toggle('is-bad', Boolean(bad));
  }

  render(data) {
    // BOOKED-IN LINES OUTLIVE A RELOAD WHILE THIS TAB IS ON SCREEN (Jonathan, 28/10/2026). Somebody
    // working down the list keeps their green lines, Undo and all - the same elements, so Undo still
    // works - in their supplier's box, until the page is refreshed. But a reload while they are on
    // ANOTHER tab (they went and approved a delivery) is the refresh: it starts clean, and booked
    // lines and finished suppliers go. A part-arrival's remainder is drawn fresh below its line.
    const onScreen = this.getClientRects().length > 0;
    const booked = !onScreen ? [] : [...this.panel.querySelectorAll('.stock-processing__row.is-arrived')].map((el) => {
      const box = el.closest('.stock-processing__supplier');
      const name = box && box.querySelector('.stock-processing__supplier-name');
      return { el, supplier: name ? name.textContent : '' };
    });
    this.renderList(data);
    if (!booked.length) return;
    const empty = this.panel.querySelector('.stock-processing__empty');
    if (empty) empty.remove();
    for (const b of booked) {
      let box = [...this.panel.querySelectorAll('.stock-processing__supplier')].find((x) => {
        const name = x.querySelector('.stock-processing__supplier-name');
        return (name ? name.textContent : '') === b.supplier;
      });
      if (!box) {
        box = document.createElement(b.supplier ? 'details' : 'section');
        box.className = 'stock-processing__supplier';
        if (b.supplier) {
          box.open = true;
          const name = document.createElement('summary');
          name.className = 'stock-processing__supplier-name';
          name.textContent = b.supplier;
          box.appendChild(name);
        }
        const fresh = document.createElement('div');
        fresh.className = 'stock-processing__list';
        box.appendChild(fresh);
        this.panel.appendChild(box);
      }
      const list = box.querySelector('.stock-processing__list');
      const remainder = list.querySelector(`.stock-processing__row[data-till-id="${CSS.escape(String(b.el.dataset.tillId))}"]:not(.is-arrived)`);
      if (remainder) list.insertBefore(b.el, remainder); else list.appendChild(b.el);
    }
  }

  renderList(data) {
    const waiting = data.awaiting || [];
    // GROUPED BY SUPPLIER (Jonathan, 15/08/2026). Staff think in deliveries from a
    // supplier - "what are we expecting from Asmodee" - so each supplier gets its own
    // box. The backend groups it; this only draws it, and falls back to one unnamed
    // group so an older backend still renders.
    const groups = data.suppliers && data.suppliers.length
      ? data.suppliers
      : (waiting.length ? [{ supplier: '', lines: waiting }] : []);
    this.panel.innerHTML = '';

    if (!waiting.length) {
      this.panel.innerHTML =
        '<p class="stock-processing__empty">Nothing is waiting to be booked in.</p>';
      return;
    }

    this.panel.appendChild(this.heading(`Awaiting delivery (${waiting.length})`));

    // COLLAPSIBLE PER SUPPLIER (Jonathan, 19/09/2026). A <details> box, open by default, so
    // somebody booking in one supplier's boxes can fold the others away. The page redraws on
    // every refresh and after every tick, so which suppliers are folded is remembered here
    // and in this browser - otherwise each Arrived would spring them all open again.
    const folded = this.foldedSuppliers();
    groups.forEach((group) => {
      const box = document.createElement(group.supplier ? 'details' : 'section');
      box.className = 'stock-processing__supplier';
      if (group.supplier) {
        box.open = !folded.has(group.supplier);
        const name = document.createElement('summary');
        name.className = 'stock-processing__supplier-name';
        name.textContent = group.supplier;
        box.appendChild(name);
        box.addEventListener('toggle', () => {
          if (box.open) folded.delete(group.supplier);
          else folded.add(group.supplier);
          this.saveFoldedSuppliers(folded);
        });
      }
      const list = document.createElement('div');
      list.className = 'stock-processing__list';
      (group.lines || []).forEach((row) => list.appendChild(this.line(row)));
      box.appendChild(list);
      this.panel.appendChild(box);
    });
  }

  // A per-browser convenience only: storage can be blocked, and then every box starts open.
  foldedSuppliers() {
    if (!this.folded) {
      let saved = [];
      try {
        saved = JSON.parse(localStorage.getItem('stock-processing-folded') || '[]');
      } catch (err) {
        saved = [];
      }
      this.folded = new Set(Array.isArray(saved) ? saved : []);
    }
    return this.folded;
  }

  saveFoldedSuppliers(folded) {
    try {
      localStorage.setItem('stock-processing-folded', JSON.stringify([...folded]));
    } catch (err) {
      // Nothing to do: the fold still holds until the page is reloaded.
    }
  }

  heading(text) {
    const h = document.createElement('h2');
    h.className = 'stock-processing__section-heading';
    h.textContent = text;
    return h;
  }

  /**
   * What the Reserved box opens on, how high it may be stepped, and how much of it is new.
   *
   * WHAT IS ALREADY BEING HELD BACK FOR THIS PRODUCT, whether or not it is a pre-order
   * (Jonathan, 07/09/2026: "We always want the reserve record to match the website wherever
   * it appears").
   *
   * TWO SOURCES FOR ONE FIGURE, AND THE LIVE ONE WINS. A flagged pre-order carries the
   * website's own `safety_stock`, read a moment ago; everything else takes it from
   * `dice_cup_reserved` through the backend, which `Reservations Discovery` keeps matched to
   * the website hourly. Before this, only the first existed - so the Destined Rivals ETB, 70
   * of which the website was holding, opened this box at ZERO while the Reservations tab one
   * tab over said 70.
   *
   * RESERVED IS HELD PLUS COMMITTED (Jonathan, 30/08/2026): "Preorders that we've already
   * sold on the website should list the quantity of sold orders in the committed bucket...
   * This is effectively reserved stock." The Reservations tab adds the two the same way, and
   * the RECORD stores the held part alone - so both pages do this arithmetic at display time
   * and cannot end up showing different numbers.
   *
   * `presold` IS ZERO FOR ANYTHING THAT IS NOT A PRE-ORDER, because nothing on this page asks
   * Shopify for a committed count outside that list. That understates rather than overstates,
   * which is the same safe direction the Reservations tab takes when Shopify cannot be asked
   * at all: it never claims stock is spoken for when it might not be. Measured 07/09/2026
   * across every product then awaiting delivery - 35 of them - and not one carried a
   * committed unit outside the pre-order list.
   *
   * AND A RESERVATION ALREADY TYPED AGAINST THIS DELIVERY WINS, whatever the website says.
   * That figure has not been applied yet - it is an instruction waiting for the stock to be
   * listed - so it is the only place it exists, and a box that opened at something else would
   * quietly overwrite what somebody decided.
   */
  static reservedFigures(row, preOrder) {
    const presold = preOrder ? Math.max(0, Number(preOrder.presold) || 0) : 0;
    const held = preOrder
      ? Math.max(0, Number(preOrder.safety) || 0)
      : Math.max(0, Math.round(Number((row || {}).held) || 0));
    // A PRE-RELEASE KIT'S PRESOLD IS ALREADY INSIDE WHAT IS HELD (17/10/2026). Its presold is the kits
    // the event tab has taken, and those kits are part of the forecast held back on the website -
    // unlike a website order, which Shopify keeps outside the held figure. Counting them twice made
    // the Delta Reign kit's box open at 523 against 280 arriving.
    const inHeld = preOrder ? Math.min(presold, Math.max(0, Number(preOrder.presoldHeld) || 0)) : 0;
    const spokenFor = held + presold - inHeld;
    const typed = Math.max(0, Math.round(Number((row || {}).reserved) || 0));
    // A HIGH-DEMAND PRODUCT OPENS WITH ALL OF IT RESERVED (Jonathan, 14/09/2026): "It means the
    // staff who are processing the stock's arrival in store can see that all of the product has
    // been reserved and none of it is available for sale on the website." ALL OF IT is what the
    // product will hold once this delivery is in: for a pre-order the till's stock plus what is
    // arriving (never less than is already spoken for), for anything else what is already held
    // plus what is arriving. The sync and the pre-order landing are what actually hold it back;
    // this is so the box says so. A figure somebody typed against the delivery still wins.
    // `highDemand` null means the backend could not tell, and the box opens as before.
    let reservedNow = typed > 0 ? typed : spokenFor;
    if (typed <= 0 && row && row.highDemand === true) {
      const arriving = Math.max(0, Math.round(Number(row.quantity) || 0));
      const all = preOrder
        ? Math.max(0, Math.round(Number(preOrder.tillStock) || 0)) + arriving
        : held + arriving;
      reservedNow = Math.max(spokenFor, all);
    }
    return { presold, held, spokenFor, typed, reservedNow };
  }

  /** One awaiting line: what we expect, what actually turned up, and Arrived. */
  line(row) {
    const expected = Number(row.quantity) || 0;
    // THE PRE-ORDER, IF THIS PRODUCT IS ONE. Everything it drives is drawn from the
    // website's own figures rather than from the delivery note: what has been presold, and
    // what the shop is already holding back.
    const preOrder = (this.preOrders && this.preOrders.get(Number(row.tillId))) || null;
    const tillStock = preOrder ? Number(preOrder.tillStock) || 0 : 0;
    // EVERY FIGURE THE RESERVED BOX TURNS ON, worked out in one place - see
    // `reservedFigures`. It is arithmetic that moves real stock, so it is a pure static
    // method a test can exercise without building a page.
    const figures = StockProcessing.reservedFigures(row, preOrder);
    const presold = figures.presold;
    const spokenFor = figures.spokenFor;
    const reservedNow = figures.reservedNow;
    // MORE SOLD THAN THIS DELIVERY AND THE SHELF TOGETHER CAN COVER (his rule, refined the
    // same day to include the till: "we only need to flag the warning triangle if the
    // pre-sold stock is greater than the expected stock plus the current Till stock"), so a
    // second batch does not warn falsely when the first is already in.
    const short = presold > expected + tillStock;
    const item = document.createElement('div');
    item.className = 'stock-processing__row';
    item.dataset.rowId = row.rowId;
    item.dataset.tillId = row.tillId;

    item.innerHTML =
      // THE PICTURE, so whoever is counting can see what they are looking for
      // (Jonathan, 25/08/2026). It is the first thing in the row because that is the
      // order the job happens in: find the thing in the box, then count it.
      //
      // A PRODUCT WITH NO PICTURE GETS AN EMPTY FRAME, not a missing cell, so the rows
      // stay aligned down the list. There is deliberately no way to ADD one from here -
      // that belongs on the Deliveries page, where somebody is deciding what the
      // product IS; this page is for counting boxes that have already been decided.
      `<div class="stock-processing__image">${imageHtml(row)}</div>` +
      // THE PO MARK FOLLOWS THE NAME ON ITS OWN LINE (Jonathan, 15/09/2026). The name box is a
      // column, so both sit inside one span or the mark becomes a line of its own.
      `<div class="stock-processing__name"><span class="stock-processing__title">${escapeHtml(row.productName || '')}`
        + (preOrder ? `<span class="dc-po stock-processing__po" title="Pre-order">PO</span>` : '') + `</span>`
        + StockProcessing.dateLine(this.dates && this.dates.get(Number(row.tillId)),
          new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/London' }))
        + `</div>` +
      // EXPECTED IS SHOWN SEPARATELY AND IS NOT EDITABLE (Jonathan's spec). It is
      // the only thing on the row that says what SHOULD have come, so it must not
      // move when somebody edits the count.
      // TWO LINES NOW, ONE UNDER THE OTHER (Jonathan, 29/08/2026): "add another line
      // under the numbers and the plus/minus buttons... that says Reserved:". The
      // arrived count on top, the reservation below it, each with its own box and
      // steppers, so the two are never mistaken for one another.
      //
      // RESERVED DEFAULTS TO ZERO, NOT TO THE QUANTITY. Most deliveries are for the
      // shelf; a reservation is the exception, and a box pre-filled with the whole
      // delivery would quietly hold everything back the moment somebody mis-tapped.
      `<div class="stock-processing__figures">` +
        `<div class="stock-processing__figure">` +
          `<span class="stock-processing__label">Expected: <b>${expected}</b></span>` +
          `<div class="stock-processing__count">` +
            `<button type="button" data-step="-1" aria-label="One fewer">&minus;</button>` +
            `<input type="number" inputmode="numeric" min="0" value="${expected}" data-count>` +
            `<button type="button" data-step="1" aria-label="One more">+</button>` +
          `</div>` +
        `</div>` +
        `<div class="stock-processing__figure" data-reserved-figure>` +
          `<span class="stock-processing__label">Reserved:</span>` +
          `<div class="stock-processing__count">` +
            `<button type="button" data-reserve-step="-1" aria-label="Reserve one fewer">&minus;</button>` +
            `<input type="number" inputmode="numeric" min="${presold}" value="${reservedNow}" data-reserved>` +
            `<button type="button" data-reserve-step="1" aria-label="Reserve one more">+</button>` +
          `</div>` +
        `</div>` +
      `</div>` +
      // PRESOLD, ON A PRE-ORDER ONLY. It is not a figure about this delivery - it is how
      // many customers are already waiting - so it sits on its own line rather than beside
      // the counts, and ordinary deliveries never show it.
      (preOrder
        ? `<p class="stock-processing__presold">Presold: <b>${presold}</b>` +
          (short
            ? ` <span class="dc-warn" title="Expected stock is less than presold stock!">&#9888;</span>`
            : '') +
          `</p>`
        : '') +
      // THE TWO BUTTONS SHARE ONE GRID CELL, stacked. The row is a four-column grid, so a
      // second button as its own child became a fifth column and shunted the pair onto a
      // line of their own, full width. Wrapping them keeps the row exactly as it was and
      // puts Reserve under Arrived, which is where he asked for it.
      `<div class="stock-processing__actions">` +
        `<div class="stock-processing__booked" data-booked>` +
          `<button type="button" class="stock-processing__arrived" data-arrived>Arrived</button>` +
          // UNDO, SHOWN ONLY ONCE THIS LINE HAS BEEN BOOKED IN (Jonathan, 23/10/2026): a tap
          // made by mistake is taken back by whoever made it, on the spot.
          `<button type="button" class="stock-processing__undo" data-undo hidden>Undo</button>` +
        `</div>` +
        // RESERVE HOLDS STOCK BACK BEFORE THE DELIVERY LANDS (Jonathan, 30/08/2026): "if a
        // customer has reserved one copy of a game and the delivery note has 3, we want to
        // reserve 1, but make the other 2 available for preorder."
        //
        // IT WRITES THE FIGURE TO THE DELIVERY LINE AND NOTHING ELSE - not Shopify, not the
        // reservations record. Nothing can be held back until there is something to hold,
        // and the listing step subtracts it as it creates the listing, so the shop never
        // offers the reserved copy even for a moment.
        `<button type="button" class="stock-processing__reserve" data-reserve>Reserve</button>` +
      `</div>` +
      `<p class="stock-processing__over" data-over hidden></p>`;

    const input = item.querySelector('[data-count]');
    const reservedInput = item.querySelector('[data-reserved]');

    // CAPPED AT WHAT ARRIVED OR AT WHAT IS ALREADY SPOKEN FOR, whichever is larger.
    // Reserving more than either is arithmetic that cannot be true of this delivery, so
    // the box will not go past it. The Reservations tab can still reserve beyond stock,
    // where it means "and more is coming"; here the boxes are on the floor being counted.
    item.querySelectorAll('[data-reserve-step]').forEach((button) => {
      button.addEventListener('click', () => {
        // THE CEILING IS WHAT IS REALLY SPOKEN FOR, not what this delivery brings: the
        // website may be holding stock from an earlier batch, and capping at this note's
        // quantity would silently give some of it away. That was written for a pre-order
        // and is true of ANY product the shop is already holding stock back for, which is
        // why there is one ceiling here now rather than a pre-order branch.
        //
        // A TYPED INSTRUCTION RAISES IT TOO. Somebody set that figure deliberately; a
        // ceiling that ignored it would snap the box down to the delivery quantity the
        // first time anyone touched a stepper.
        const most = Math.max(spokenFor, reservedNow, Number(input.value) || 0, 0);
        // AND THE FLOOR IS THE COMMITTED COUNT (his ruling, 30/08/2026). Those units are on
        // real customer orders and cannot be un-reserved by typing a smaller number, so the
        // box will not go below them - only the held part is editable.
        const next = (Number(reservedInput.value) || 0) + Number(button.dataset.reserveStep);
        reservedInput.value = Math.min(most, Math.max(presold, next));
      });
    });

    item.querySelectorAll('[data-step]').forEach((button) => {
      button.addEventListener('click', () => {
        // NOT capped at what was expected (Jonathan, 12/08/2026): "if the staff
        // in-store mark that we've received 10 when we were only expecting 6, then
        // we want 10 to go on the stock. We want the stock to be accurate." The
        // over-delivery is flagged to a manager by the backend; it is never refused,
        // because the boxes are physically in the shop either way.
        input.value = Math.max(0, (Number(input.value) || 0) + Number(button.dataset.step));
        flagOver();
      });
    });

    // Says plainly that more turned up than was expected, so it is a deliberate
    // act rather than a typo nobody noticed. It does not block the tick.
    const flagOver = () => {
      const over = (Number(input.value) || 0) - expected;
      item.classList.toggle('is-over', over > 0);
      const note = item.querySelector('[data-over]');
      note.hidden = over <= 0;
      note.textContent = over > 0 ? `${over} more than expected - a manager will be told` : '';
    };
    input.addEventListener('input', flagOver);

    item.querySelector('[data-arrived]').addEventListener('click', () => {
      // WHAT THE TICK RESERVES IS THE INCREASE, NOT THE BOX (07/09/2026). `arrived` hands
      // its figure to `reservations-ops` as an `add`, which adds to what the website
      // REALLY holds - so passing a box that now OPENS on what is already held back would
      // hold the same stock a second time, and the Destined Rivals ETB would have gone
      // from 70 to 140 on the first tick. Only the part above what was already spoken for
      // is new, and a box nobody touched therefore reserves nothing.
      //
      // THIS WAS ALREADY WRONG FOR A PRE-ORDER and nobody had hit it: that box has opened
      // on `safety_stock + committed` since 30/08/2026 and the tick added the whole of it
      // back on top. Generalising the pre-fill is what made it certain rather than likely.
      const box = Math.max(0, Number(reservedInput.value) || 0);
      this.arrived(item, row, Math.max(0, Number(input.value) || 0),
                   Math.max(0, box - spokenFor));
    });

    item.querySelector('[data-reserve]').addEventListener('click', () => {
      this.reserveAhead(item, row, Math.max(0, Number(reservedInput.value) || 0));
    });

    item.querySelector('[data-undo]').addEventListener('click', () => this.undo(item, row));
    return item;
  }

  /** Offer Undo for what this tick just booked in, and remember what to take back. */
  showUndo(item, quantity) {
    item.booked = { quantity };
    item.querySelector('[data-undo]').hidden = false;
    item.querySelector('[data-booked]').classList.add('has-undo');
  }

  /**
   * TAKE BACK AN ARRIVED TICK MADE BY MISTAKE (Jonathan, 23/10/2026).
   *
   * A SEPARATE WORKFLOW, `Stock Processing Undo`, for the reason Reservations Ops is one: the
   * arrival path must not change for the sake of its own undo. It puts the quantity back into
   * on order on the till and puts the line back on this list; the sync then takes it off the
   * website, as it put it there. It refuses (and says why) once some of it has sold.
   *
   * A RESERVATION THE TICK MADE IS NOT TAKEN BACK FROM HERE, deliberately. Doing so moves the
   * unit from held to FOR SALE on the website before the sync has removed it - on a live
   * high-demand listing that is a buyable copy the shop does not have. The reservation record
   * follows the website's held figure by itself (Reservations.reconcile.js), so once the sync
   * takes the unit off, the reservation goes with it.
   */
  async undo(item, row) {
    const booked = item.booked;
    if (!booked || !(booked.quantity > 0)) return;
    const button = item.querySelector('[data-undo]');
    const arrived = item.querySelector('[data-arrived]');
    if (!this.claim(row.tillId, button)) return;
    button.textContent = 'Undoing…';
    try {
      const answer = await (window.dcFetch || fetch)(this.url.replace(/stock-processing-ops/, 'stock-processing-undo'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ secret: this.secret, rowIds: row.rowIds, tillId: row.tillId,
                               quantity: booked.quantity, staffName: this.dataset.customerName || '' }),
      });
      const text = await answer.text();
      if (!text.trim()) throw new Error(`undo failed (${answer.status})`);
      const data = JSON.parse(text);
      if (data.busy || data.error) throw new Error(String(data.error || 'it could not be undone'));
      item.booked = null;
      item.classList.remove('is-arrived', 'is-over');
      item.querySelector('[data-booked]').classList.remove('has-undo');
      button.hidden = true;
      arrived.textContent = 'Arrived';
      arrived.disabled = false;
      const note = item.querySelector('[data-over]');
      note.hidden = true;
      note.textContent = '';
      this.say('');
    } catch (err) {
      this.say(`Could not undo ${row.productName}: ${err.message}`, true);
    } finally {
      button.textContent = 'Undo';
      this.release(row.tillId, button);
      button.disabled = false;
    }
  }

  /**
   * Record a reservation made on the delivery line, AFTER the stock has landed.
   *
   * A SEPARATE CALL TO A SEPARATE WORKFLOW, and deliberately so. The arrival write is the
   * one that must not fail - it is what puts the boxes on the till - so nothing about
   * reserving is allowed upstream of it. If this call fails the stock is still booked in
   * correctly and the row says the reservation did not save, which is a small thing to
   * redo on the Reservations tab.
   *
   * `add`, NOT `set`: two deliveries of the same product can each carry a reservation for
   * a different customer, and the second must not erase the first.
   */
  /**
   * Hold part of a delivery back before it arrives.
   *
   * THE ROW STAYS ON THE LIST. Reserving is not booking in - the boxes are still to come,
   * and whoever presses this is recording a customer's claim on them, not counting them.
   * So the row goes green and keeps its Arrived button.
   *
   * IT WRITES ONLY TO THE DELIVERY LINE. The listing step reads that figure and creates the
   * listing already holding it back, which is what stops the reserved copy being on sale
   * even briefly - his instruction: "the mechanism that adds the stock needs to add 1 less
   * if 1 is reserved."
   */
  async reserveAhead(item, row, reserved) {
    const button = item.querySelector('[data-reserve]');
    // AMBER WHILE SETTING, GREEN ONCE SET, AND GREEN UNTIL THE PAGE IS REFRESHED - his
    // instruction, and "like stock checking", so it is the same two classes and the same
    // two colours that page uses. A colour on the box the number is in says which figure
    // went through; a line of text at the top of the page does not, which is why he asked
    // for no confirmation message.
    const figure = item.querySelector('[data-reserved-figure]');
    figure.classList.add('is-dirty');
    figure.classList.remove('is-saved');
    button.disabled = true;
    try {
      await this.call('reserve', { rowIds: row.rowIds, tillId: row.tillId, reserved });
      button.disabled = false;
      figure.classList.remove('is-dirty');
      figure.classList.add('is-saved');
      this.say('');
    } catch (err) {
      button.disabled = false;
      // NEITHER COLOUR ON A FAILURE. Amber would claim it is still going and green would
      // claim it worked; the box goes back to plain and the error says what happened, so
      // the figure can simply be pressed again.
      figure.classList.remove('is-dirty', 'is-saved');
      this.say(`Could not reserve ${row.productName}: ${err.message}`, true);
    }
  }

  async reserve(row, reserved) {
    const url = this.dataset.reserveUrl;
    if (!url || !reserved) return null;
    const answer = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        secret: this.dataset.reserveSecret || this.secret,
        operation: 'add',
        productId: row.tillId,
        reserved,
        staffName: this.dataset.customerName || '',
      }),
    });
    if (!answer.ok) throw new Error(`reserving failed (${answer.status})`);
    const text = await answer.text();
    if (!text.trim()) throw new Error('reserving answered with nothing at all');
    return JSON.parse(text);
  }

  /**
   * Has this arrival completed a pre-order? If so the website is squared to the till and
   * the flag comes off; if it was a part-batch, nothing happens.
   *
   * NEVER A SWEEP. A scheduled comparison of the two figures clears flags nobody touched -
   * it did exactly that to five products delivered long before any of this existed. Arriving
   * is an EVENT, and this is the event.
   *
   * IT CANNOT COST THE DELIVERY. It runs after the till write has already succeeded, and a
   * failure only leaves the flag on for somebody to clear by hand.
   */
  async preOrderLanded(row, item) {
    const url = this.dataset.landedUrl;
    if (!url || !this.preOrders || !this.preOrders.has(Number(row.tillId))) return;
    try {
      const answer = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ secret: this.dataset.preorderSecret || this.secret,
                               productId: row.tillId }),
      });
      if (!answer.ok) return;
      const text = await answer.text();
      if (!text.trim()) return;
      const data = JSON.parse(text);
      if (data && data.landed > 0) {
        const note = item.querySelector('[data-over]');
        note.hidden = false;
        note.textContent = 'Pre-order complete - the website has been squared to the till.';
      }
    } catch (err) {
      // Deliberately quiet: the stock is in, and the flag simply stays on.
    }
  }

  /**
   * ONE TICK IN FLIGHT PER PRODUCT, AND NEVER A BLIND RETRY (10/09/2026).
   *
   * AN ARRIVAL IS NOT IDEMPOTENT AND A REPEAT COSTS REAL STOCK. `Plan Arrival` re-reads
   * the till and writes `CurrentStock: current + arrived`, so the same tick sent twice
   * books the delivery in twice - and the sheet side absorbs it silently, because by then
   * `OnOrder` is spent, so the second pass reads as an over-delivery rather than as a
   * mistake. The stock then reaches the website on the next sync run.
   *
   * THE PAGE WAS INVITING EXACTLY THAT. On a failure it re-armed the button AND queued
   * the job for `flush` to send again - and both failures this estate has really seen
   * were lost ANSWERS rather than lost work. So a tick whose fate is unknown is settled
   * against the server before anything is sent a second time.
   *
   * THE LOCK IS PER PRODUCT (Jonathan, 15/09/2026). It used to be the whole page, so one
   * slow tick greyed out every other line. Only the product being saved is locked now;
   * two products never share a till record, so ticking a second one cannot collide with
   * the first. Two people ticking the SAME product on two tablets is stopped by the
   * backend, which claims the product before it reads anything.
   *
   * The in-memory queue is kept: the boxes are on the floor in front of whoever is
   * counting, and that is still the right place for a tick that genuinely did not save.
   * What changes is that nothing goes back on the wire without asking first.
   */
  claim(tillId, button) {
    const key = String(tillId);
    if (this.inFlight.has(key)) return false;
    this.inFlight.add(key);
    if (button) button.disabled = true;
    return true;
  }

  release(tillId, button) {
    this.inFlight.delete(String(tillId));
    // A line already booked in keeps its finished state rather than being re-armed.
    if (button && button.textContent !== 'Booked in') button.disabled = false;
  }

  /** What the server says is still owed on this product, right now. */
  async awaitingNow(tillId) {
    const data = await this.call('list', {}, true);
    const line = (data.awaiting || []).find((l) => String(l.tillId) === String(tillId));
    return line ? Math.max(0, Number(line.quantity) || 0) : 0;
  }

  /**
   * Did a tick whose answer was lost actually reach the till?
   *
   * THE AWAITING FIGURE IS THE EVIDENCE, because the arrival path spends it: a tick that
   * landed leaves less owed than it asked for. A run still writing gets there within a few
   * seconds; one that genuinely failed leaves the figure exactly where it was however long
   * we wait.
   *
   * A READ THAT FAILED IS NOT AN ANSWER and must never be read as "nothing landed" - that
   * is the reading that would license the double-booking this exists to prevent.
   */
  async settleArrival(job) {
    const deadline = Date.now() + 40000;
    let awaiting = null;
    while (Date.now() < deadline) {
      await new Promise((done) => { setTimeout(done, 4000); });
      try {
        awaiting = await this.awaitingNow(job.tillId);
      } catch (ignored) {
        continue;
      }
      if (awaiting < job.quantity) return { landed: true, awaiting };
    }
    return { landed: false, awaiting };
  }

  // `reserved` here is the INCREASE somebody typed into the Reserved box, not the total
  // it shows - see the click handler. It reaches `reservations-ops` as an `add`.
  async arrived(item, row, quantity, reserved) {
    if (quantity <= 0) {
      this.say('Enter how many actually turned up before ticking it off.', true);
      return;
    }
    // Unreachable while the button is disabled, and kept as the backstop that makes
    // one-at-a-time-per-product a property of the code rather than of the buttons.
    const button = item.querySelector('[data-arrived]');
    if (!this.claim(row.tillId, button)) return;
    try {
      await this.arrivedOnce(item, row, quantity, reserved);
    } finally {
      this.release(row.tillId, button);
    }
  }

  async arrivedOnce(item, row, quantity, reserved) {
    const button = item.querySelector('[data-arrived]');
    button.textContent = 'Saving…';

    // A product on two delivery notes is ONE line here (Jonathan, 12/08/2026), so a
    // tick can settle several sheet rows at once. The backend spends the arrived
    // amount across them oldest first.
    const job = { rowIds: row.rowIds, quantity, tillId: row.tillId };
    try {
      const answer = await this.call('arrived', job);
      // ALREADY BOOKED IN - a second tap, or another tablet got there first. Nothing was
      // written, and the line IS done, so it shows as done rather than inviting a third tap.
      if (answer && answer.alreadyBooked) {
        item.classList.add('is-arrived');
        button.textContent = 'Booked in';
        this.say(String(answer.error || 'That line has already been booked in. Refresh the page.'), true);
        return;
      }
      // SOMEBODY ELSE IS BOOKING THIS PRODUCT IN RIGHT NOW. The backend refused before
      // reading or writing anything, so the fate is KNOWN - there is nothing to settle and
      // nothing to queue. The line stays as it was and says why.
      if (answer && answer.busy) {
        button.textContent = 'Arrived';
        this.say(String(answer.error || 'Somebody is booking this product in right now.'), true);
        return;
      }
      if (answer && answer.error) throw new Error(answer.error);
      // THE ROW STAYS ON SCREEN, GREEN (Jonathan's spec). It must not vanish under
      // somebody working down a list on a tablet - the line they just touched
      // disappearing is how you lose your place and tick the wrong one next.
      item.classList.add('is-arrived');
      button.textContent = 'Booked in';
      this.showUndo(item, quantity);
      const left = Number(answer.remaining) || 0;
      if (left > 0) {
        // A short delivery keeps its remainder, and says so, rather than looking
        // finished. It reappears on the next refresh reading the smaller number.
        const label = item.querySelector('.stock-processing__label');
        if (label) label.innerHTML = `Still expected: <b>${left}</b>`;
      }
      this.say('');

      // A PRE-ORDER MAY HAVE JUST COMPLETED. Marking stock as arrived is the ONLY thing
      // besides un-ticking on the Pre-Orders tab that ends a pre-order (Jonathan,
      // 30/08/2026) - so the check is fired here, by the tick, and never by a sweep
      // comparing figures on a timer.
      //
      // IT ASKS ABOUT THIS ONE PRODUCT and it asks AFTER the till write has succeeded, so a
      // failure leaves the flag on and costs nothing. A part-delivery answers "not yet" and
      // changes nothing at all.
      await this.preOrderLanded(row, item);

      // THE STOCK IS ALREADY IN. Anything that goes wrong from here costs a reservation,
      // never the delivery - so it is reported on the row and the tick stays green.
      if (reserved > 0) {
        // NO NOTE WHEN IT WORKS (Jonathan, 23/10/2026: "we don't need it") - the Reserved box
        // already shows the figure. Only a failure is said, below.
        try {
          await this.reserve(row, reserved);
        } catch (err) {
          const note = item.querySelector('[data-over]');
          note.hidden = false;
          note.textContent = `Booked in, but the reservation did not save: ${err.message}. `
            + 'Set it on the Reservations tab.';
          item.classList.add('is-over');
        }
      }
    } catch (err) {
      button.textContent = 'Arrived';
      // NOT QUEUED UNTIL WE KNOW IT DID NOT LAND. Queueing first and asking later is how
      // a delivery gets booked in twice, and a doubled stock figure reaches the website
      // on the next sync run.
      this.say(`That did not save: ${err.message}. Checking whether it reached the till…`,
        true);
      const settled = await this.settleArrival(job);
      if (settled.landed) {
        item.classList.add('is-arrived');
        button.textContent = 'Booked in';
        this.say(`${err.message} — but that line did reach the till after all, so it has `
          + 'not been queued again. Refresh to see where the delivery stands.', true);
        return;
      }
      button.disabled = false;
      this.pending.push(job);
      this.showPending();
      this.say(`That did not save: ${err.message}`, true);
    }
  }

  showPending() {
    this.pendingBox.hidden = this.pending.length === 0;
    this.pendingText.textContent = this.pending.length === 1
      ? '1 line has not saved yet.'
      : `${this.pending.length} lines have not saved yet.`;
  }

  async flush() {
    const retry = this.querySelector('[data-retry]');
    if (this.flushing) return;
    this.flushing = true;
    if (retry) retry.disabled = true;
    try {
      await this.flushOnce();
    } finally {
      this.flushing = false;
      if (retry) retry.disabled = false;
    }
  }

  async flushOnce() {
    const queue = this.pending.slice();
    this.pending = [];
    this.showPending();
    let landed = 0;
    for (const job of queue) {
      // A PRODUCT BEING TICKED ON THIS PAGE RIGHT NOW IS LEFT IN THE QUEUE for the next
      // retry, and one being retried is locked like a tick - the same per-product rule.
      if (!this.claim(job.tillId)) { this.pending.push(job); continue; }
      try {
        // NEVER SEND MORE THAN THE SERVER STILL SAYS IS OWED. A queued tick is one whose
        // answer went missing, not one known to have failed - so if less is now awaiting
        // than this job asked for, the first attempt reached the till and sending it
        // again would add the delivery a second time.
        //
        // THE CONSERVATIVE DIRECTION IS TO SKIP, and the two mistakes are not equal: a
        // tick wrongly skipped is re-tickable by somebody standing in front of the boxes,
        // where a delivery booked in twice inflates the shop's stock silently and reaches
        // the website on the next sync run.
        const awaiting = await this.awaitingNow(job.tillId);
        if (awaiting < job.quantity) { landed += 1; continue; }
        // An answer carrying an error - including "somebody is booking this in right now" -
        // did not book anything, so the job stays queued rather than being dropped as sent.
        const answer = await this.call('arrived', job);
        // Already booked in means the line is done, so it is not queued again.
        if (answer && answer.alreadyBooked) { landed += 1; continue; }
        if (answer && answer.error) throw new Error(answer.error);
      } catch (err) {
        this.pending.push(job);
      } finally {
        this.release(job.tillId);
      }
    }
    this.showPending();
    if (landed) {
      this.say(`${landed} of those had already reached the till, so ${landed === 1
        ? 'it was' : 'they were'} not sent again.`, true);
    }
    if (!this.pending.length) this.load();
  }
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
}

// THE PICTURE CELL IS SHARED (`staff-pictures.js`), so a product with several pictures shows
// the same count and opens the same gallery on every staff page. The `none` marker and the
// empty frame for a product with no picture are handled there too.
function imageHtml(row) {
  return window.StaffPictures.html(row, {
    className: 'stock-processing__thumb', lazy: true, title: row && row.productName,
  });
}

customElements.define('stock-processing', StockProcessing);
})();
